// @givmo/org-console — an outside RECORD's facts, read-only, under Organization details (Org Machinery
// OM-64, Design 4C §2 and §8; the frames approved by D-CPO-69).
//
// Some of an organization's facts are not its to keep: they are another record's, read as that record
// holds them, on that record's own date. The server composes the card as data (`records` on the
// settings read: the record's label, its facts, its date, how to change them, and what it says when
// it does not list the organization), so this draws exactly what was served and names no record, no
// source and no class. No control and no Edit: a record is changed where it is kept.
//
//   * Under the record's label, its detail with the record's own date: where the facts come from —
//     the card's description, as every card on the page has one (the source before the facts).
//   * A definition list of the served facts — a text as itself, an address on its own lines with the
//     in-care-of name first, as the record holds it, and a fact's served description under its value.
//   * Under the rows: the served line saying how to change them.
//   * A record that holds no row for the organization (`listed: false`) draws the facts it still has
//     (the identifier the organization is known by), then the served lines saying what was not found
//     and what happens next (M9) — or, with no fact at all, that the identifier is missing. No source
//     line and no change line: the record holds nothing to date or to change. The web words none of
//     it, so a backend that serves no lines draws the facts alone.
//
// DATA CONTRACT: the composer's `org-settings-details-record-<source>` (the card) · `-row-<fact key>` ·
// `-source` · `-change` · `-empty`.

import { Fragment } from 'react';
import { Card, CardBody, CardHeader, testIdProp } from '@givmo/design-system';
import { recordAddressLines } from './org-address';
import { formatOrgRecordDay } from './org-decode';
import type { OrgSettingRecord, OrgSettingRecordFact } from './wire-types';

/** The record's source line (D-CPO-69 (6)): its served detail, with its own date when it has one. */
export function recordSourceLine(record: OrgSettingRecord): string {
  const day = formatOrgRecordDay(record.as_of);
  return day.length > 0 ? `${record.detail}, as of ${day}.` : `${record.detail}.`;
}

function RecordFactValue({ value }: { value: NonNullable<OrgSettingRecordFact['value']> }) {
  if (typeof value === 'string') return <>{value}</>;
  return (
    <>
      {recordAddressLines(value).map((line) => (
        <span key={line} className="block">
          {line}
        </span>
      ))}
    </>
  );
}

export interface OrgSettingRecordCardProps {
  record: OrgSettingRecord;
  /** The card's testid — the composer's `org-settings-details-record-<source>`. */
  testId: string;
}

export function OrgSettingRecordCard({ record, testId }: OrgSettingRecordCardProps) {
  // A fact the record holds no value for is not drawn: the card shows what the record says.
  const facts = record.facts.filter((fact) => fact.value !== null);
  return (
    <Card {...testIdProp(testId)}>
      <CardHeader>
        <div className="min-w-0">
          <h3 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
            {record.label}
          </h3>
          {record.listed && (
            <p
              className="mt-2 font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
              {...testIdProp(`${testId}-source`)}
            >
              {recordSourceLine(record)}
            </p>
          )}
        </div>
      </CardHeader>
      <CardBody className="flex flex-col gap-16">
        {facts.length > 0 && (
          <dl className="grid grid-cols-[160px_minmax(0,1fr)] items-baseline gap-x-24 gap-y-12">
            {facts.map((fact) => (
              <Fragment key={fact.key}>
                <dt className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                  {fact.label}
                </dt>
                <dd
                  className="min-w-0 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong"
                  {...testIdProp(`${testId}-row-${fact.key}`)}
                >
                  {fact.value !== null && <RecordFactValue value={fact.value} />}
                  {fact.description !== null && (
                    <span className="mt-4 block text-s13 text-text-tertiary">
                      {fact.description}
                    </span>
                  )}
                </dd>
              </Fragment>
            ))}
          </dl>
        )}
        {record.listed
          ? record.change_hint.length > 0 && (
              // How to change them, under the facts: the hint line's place on every card.
              <p
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                {...testIdProp(`${testId}-change`)}
              >
                {record.change_hint}
              </p>
            )
          : record.not_listed_lines.length > 0 && (
              // What was not found first, in the card's secondary text; what happens next after it.
              <div
                className="flex flex-col gap-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo"
                {...testIdProp(`${testId}-empty`)}
              >
                {record.not_listed_lines.map((line, index) => (
                  <p
                    key={line}
                    className={index === 0 ? 'text-text-secondary' : 'text-text-tertiary'}
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}
      </CardBody>
    </Card>
  );
}
